// src/pages/NavigatorEscalationsPage.jsx
// Route: /escalations (health navigator)
//
// The navigator's working queue of CRITICAL escalations — what a doctor has
// handed over, what still needs a first response, and what is being worked.
// The navigator does the outreach: message the patient, book the specialist a
// doctor suggested, acknowledge, resolve. The mirror of DoctorEscalationsPage,
// which is the doctor's version of the same queue; they share EscalationQueue.
//
// The Dashboard no longer embeds the queue — it shows the counts and links here.
//
// Three tabs: Escalations, Notes, and AI Actions to Review (the reports the AI
// has flagged for a navigator to look at; the dashboard shows its count and
// links to ?tab=ai). The Escalations tab also takes ?queue=mine|open|progress
// and ?filter=overdue, which the dashboard's escalation cards link to. One search box filters whichever tab is open, in the browser.
//
// The overdue count in the header is a light separate fetch, refreshed through
// the queue's onChanged callback.

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import EscalationQueue from '../components/EscalationQueue';
import OpenNotesList, { PRIORITY_OPTIONS } from '../components/OpenNotesList';
import ReportReviewCard, { usePendingReviews } from '../components/ReportReviewCard';
import ListSearch from '../components/ListSearch';
import { doctorEscalationAPI } from '../services/doctorApi';
import { SLA_HOURS, isSlaBreached } from '../utils/escalationState';

export default function NavigatorEscalationsPage() {
  // null = unknown (loading, or the count could not be fetched): the badge is
  // simply omitted rather than showing a wrong zero.
  const [overdue, setOverdue] = useState(null);
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') === 'ai' ? 'ai' : 'escalations'); // escalations | notes | ai
  const [query, setQuery] = useState('');
  const [priority, setPriority] = useState('ALL'); // notes tab only
  const reviews = usePendingReviews();
  const [noteCount, setNoteCount] = useState(null);

  useEffect(() => {
    document.title = 'Recommendation and Escalations — Previu';
    return () => { document.title = 'Previu — Patient Management'; };
  }, []);

  const loadOverdue = useCallback(async () => {
    try {
      const res = await doctorEscalationAPI.list({ view: 'OPEN' });
      const rows = Array.isArray(res.data) ? res.data : [];
      setOverdue(rows.filter(isSlaBreached).length);
    } catch {
      setOverdue(null);
    }
  }, []);

  useEffect(() => { loadOverdue(); }, [loadOverdue]);

  return (
    <AppShell breadcrumbs={[{ label: 'Recommendation and Escalations' }]}>
      <div className={`page-header fade-in-up${tab === 'notes' ? ' escalations-header--notes' : ''}`}>
        <div>
          <div className="page-title">Recommendation and Escalations</div>
          <div className="page-subtitle">
            Critical results that need a response within {SLA_HOURS} hours. Contact the patient,
            and book any specialist a doctor has suggested.
          </div>
        </div>
      </div>

      <div className="tabs-bar fade-in-up delay-1" role="tablist" aria-label="Recommendation and Escalations">
        <button type="button" role="tab" aria-selected={tab === 'escalations'}
          className={`tab-btn${tab === 'escalations' ? ' active' : ''}`}
          onClick={() => setTab('escalations')}>
          Escalations
        </button>
        <button type="button" role="tab" aria-selected={tab === 'notes'}
          className={`tab-btn${tab === 'notes' ? ' active' : ''}`}
          onClick={() => setTab('notes')}>
          Notes{noteCount != null ? ` (${noteCount})` : ''}
        </button>
        <button type="button" role="tab" aria-selected={tab === 'ai'}
          className={`tab-btn${tab === 'ai' ? ' active' : ''}`}
          onClick={() => setTab('ai')}>
          AI Actions to Review{reviews.items.length > 0 ? ` (${reviews.items.length})` : ''}
        </button>
      </div>

      <ListSearch id="esc-search" label="Search recommendations and escalations"
        placeholder={tab === 'notes' ? 'Search notes by patient or description'
          : tab === 'ai' ? 'Search reports by patient' : 'Search escalations by patient or alert'}
        value={query} onChange={setQuery}
        filters={tab === 'notes' && (
          <select className="form-select" aria-label="Filter notes by priority"
            value={priority} onChange={e => setPriority(e.target.value)}>
            <option value="ALL">All priorities</option>
            {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        )} />

      {tab === 'escalations' ? (
        <section className="card fade-in-up delay-1">
          <div className="card-title">
            Escalations
            {overdue > 0 && <span className="badge badge-critical">{overdue} overdue</span>}
          </div>
          <EscalationQueue mode="navigator" onChanged={loadOverdue} query={query}
            initialTab={['mine', 'open', 'progress'].includes(params.get('queue')) ? params.get('queue') : undefined}
            overdueOnly={params.get('filter') === 'overdue'} />
        </section>
      ) : tab === 'ai' ? (
        <section className="card fade-in-up delay-1">
          <div className="card-title">AI Actions to Review</div>
          <ReportReviewCard bare limit={50} query={query} reviews={reviews} />
        </section>
      ) : (
        <section className="card fade-in-up delay-1 notes-panel">
          <div className="card-title">Notes</div>
          <OpenNotesList
            patientPath={id => `/patients/${id}`}
            onLoaded={setNoteCount}
            query={query}
            priority={priority}
          />
        </section>
      )}
    </AppShell>
  );
}
